/* Dangerous Goods UN/ID picker — extracted from newquote.js (2026-10-28) so
   the SmartQuote review modal prices DG the same way New Quote does. Pure
   props: the caller owns the catalogue (`items`, from /api/elcapp/dg-items)
   and the picked list ({id, un_id, name}); the search box, its focus and the
   suggestion list live here. Backend picks the count source from the rate's
   dgUnit: PACK rates use sum(pieces.qty); UN rates use the picked-items
   count; SHIPMENT charges once. */
import { useState, useEffect, useMemo, useRef, C } from '../shared/ui.js';

const DgPicker = ({ items, picked, setPicked }) => {
  const [query,   setQuery]   = useState('');
  const [focused, setFocused] = useState(false);
  const boxRef = useRef(null);

  // Outside-click closes the suggestion dropdown.
  useEffect(() => {
    if (!focused) return;
    const h = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setFocused(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [focused]);

  // Filter the catalogue against the search box. UN/ID and name both match —
  // customer who knows neither can still find by typing partial name.
  const filtered = useMemo(() => {
    const raw = (query || '').trim().toLowerCase();
    if (!raw) return [];
    // un_id is stored bare ("1263") but displayed with the "UN" prefix.
    // Strip a leading "un" / "un " from the query so typing "UN1263" or
    // "un 1263" matches the same row as "1263".
    const q = raw.replace(/^un\s*/, '');
    const pickedIds = new Set(picked.map(x => x.id));
    return (items || []).filter(it =>
      !pickedIds.has(it.id) && (
        (it.un_id || '').toLowerCase().includes(q) ||
        (it.name  || '').toLowerCase().includes(raw)
      )
    ).slice(0, 50);
  }, [items, picked, query]);

  const pick   = it => { setPicked(p => [...p, it]); setQuery(''); };
  const remove = id => setPicked(p => p.filter(x => x.id !== id));

  return (
    <div style={{ marginBottom: 14, padding: '12px 14px',
                  background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.25)',
                  borderRadius: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>
          ⚠ Dangerous Goods · UN/ID classification
        </span>
        <span style={{ fontSize: 10, color: C.textD }}>
          {picked.length} item{picked.length === 1 ? '' : 's'} selected
          · UN-based rates charge per item · PACK rates use Total Pieces below
        </span>
      </div>
      {/* Selected chips */}
      {picked.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {picked.map(it => (
            <span key={it.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                                      padding: '3px 8px 3px 10px', background: '#fff',
                                      border: `1px solid ${C.brd}`, borderRadius: 14, fontSize: 11 }}>
              <span className="mono bc" style={{ fontWeight: 700, color: '#dc2626' }}>UN{it.un_id}</span>
              <span>{it.name}</span>
              <button onClick={() => remove(it.id)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.textD, fontSize: 13, padding: 0, lineHeight: 1 }}
                      title="Remove">×</button>
            </span>
          ))}
        </div>
      )}
      {/* Search box + suggestions */}
      <div style={{ position: 'relative' }} ref={boxRef}>
        <input type="text"
               placeholder={(items || []).length === 0 ? 'Loading DG list…' : 'Search UN/ID or shipping name… (e.g. 1090, acetone)'}
               value={query}
               onFocus={() => setFocused(true)}
               onChange={e => { setQuery(e.target.value); setFocused(true); }}
               disabled={(items || []).length === 0}
               style={{ fontSize: 12 }} />
        {focused && query && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff',
                        border: `1px solid ${C.brd}`, borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,.12)',
                        maxHeight: 280, overflowY: 'auto', marginTop: 2 }}>
            {filtered.length === 0
              ? <div style={{ padding: '10px 12px', fontSize: 12, color: C.textD }}>No matches</div>
              : filtered.map(it => (
                  <div key={it.id}
                       onMouseDown={() => pick(it)}
                       style={{ padding: '7px 12px', cursor: 'pointer', fontSize: 12,
                                borderBottom: `1px solid ${C.brd}`, display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span className="mono bc" style={{ fontWeight: 700, color: '#dc2626', minWidth: 60 }}>UN{it.un_id}</span>
                    <span style={{ color: C.text }}>{it.name}</span>
                  </div>
                ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Match stored {un_id, name} entries (saved quote / draft / AI-found UN
// numbers) back to catalogue rows so the chips carry real ids; an item no
// longer in the catalogue keeps a synthetic negative id and still rides the
// payload as {un_id, name}. Shared by New Quote's clone / Recent restore and
// the SmartQuote modal.
const matchDgItems = (wanted, items) => {
  const picked = [];
  (wanted || []).forEach((x, i) => {
    const m = items.find(it => it.un_id === x.un_id && (!x.name || it.name === x.name))
           || items.find(it => it.un_id === x.un_id)
           || { id: -(i + 1), un_id: x.un_id, name: x.name || '' };
    if (!picked.some(p => p.id === m.id)) picked.push(m);
  });
  return picked;
};

export { DgPicker, matchDgItems };
